// 맵별 분위기(ART-BIBLE 3·4장, 콘셉트 v-overview·style-B-village·r1-dungeon).
// 마을: 늦은 오후. 해를 낮게 화면 위쪽(북쪽 -z) 뒤에 두어 캐릭터·건물 윤곽에 역광이 걸리고 그림자가 카메라 쪽(+z)으로 길게 눕는다.
//       키는 주황빛, 그늘은 청록 하늘빛(반구광 하늘색)이 채워 따뜻함·차가움 대비를 만든다. 노출은 정오보다 낮게.
// R1: 맑은 낮. 해는 왼쪽 뒤 위(콘셉트 r1-dungeon의 빛줄기 방향), 그늘은 이끼 녹청.
import { VILLAGES, type MapDef, type MapId, type StageMapKey } from '../../shared/levels';
export interface MapMood {
  bg: number;
  fog: number;
  fogNear: number;
  fogFar: number;
  hemiSky: number;
  hemiGround: number;
  hemi: number;
  sun: number;
  sunI: number;
  /** 카메라 목표점 기준 해 위치(m) */
  sunX: number;
  sunY: number;
  sunZ: number;
  bloom: number;
  threshold: number;
  /** 원경·낭떠러지 안개색 */
  haze: number;
  /** 톤매핑 노출 */
  exposure: number;
  /** 화면 공간 햇무리(해 쪽 화면 모서리에서 퍼지는 가산광): 색, 세기, 중심(화면 uv, 0,0 = 왼쪽 위) */
  glow: number;
  glowI: number;
  glowX: number;
  glowY: number;
  /** 최종 채도 배율 */
  sat: number;
  /** 분할 톤(톤매핑 뒤): 어두운 쪽 곱 색, 밝은 쪽 곱 색, 세기 0..1, 검정 들어올림(그늘 색으로) */
  shadowTint: number;
  highTint: number;
  split: number;
  lift: number;
}

export const MAP_MOOD: Record<MapId, MapMood> = {
  village: {
    // 해를 더 낮게(고도 약 30°) 두어 노점·깃대·영웅 그림자가 카메라 쪽으로 길게 눕고, 반구광을 줄여 햇빛/그늘 대비를 키운다
    // 실시간 룩 조절판에서 정한 값(2026-10-14): 해를 세게, 하늘빛을 줄여 햇빛/그늘 대비를 키우고 노출을 조금 올린다
    // 색 측정(2026-10-14, 바닥 아래쪽 4분위): 목표 그늘 v0.43 s0.44 / 햇빛 v0.93 s0.45. 이전 값은 그늘 s0.61(주황 그늘)이었다
    // → 하늘빛을 푸르게·약하게, 해를 덜 주황으로(돌은 회백, 빛이 따뜻하게) 바꿔 그늘 s0.47, 햇빛 s0.43으로 맞췄다
    bg: 0xf0c48e, fog: 0xecc79c, fogNear: 34, fogFar: 108, hemiSky: 0xa4b4d0, hemiGround: 0x8a6038, hemi: 1.0,
    sun: 0xffcf98, sunI: 5.4, sunX: -11, sunY: 13, sunZ: -20, bloom: 0.55, threshold: 1.0, haze: 0xf2d2a2, exposure: 1.15,
    glow: 0xffc27a, glowI: 0.2, glowX: 0.12, glowY: -0.05, sat: 1.0,
    shadowTint: 0x8fb0c4, highTint: 0xffe6c0, split: 0.35, lift: 0.025,
  },
  r1: {
    // 햇빛(따뜻) 대 그늘(이끼 녹청) 대비를 키운다: 반구광을 줄이고 해를 세게, 왼쪽 위 화면 모서리에 옅은 햇무리
    // 색 측정(2026-10-14 던전 방3): 이전 값은 파랑이 모자라 올리브색, 햇빛 상위 10% 밝기 159(목표 197)였다
    // → 해를 복숭아빛으로 세게, 하늘빛을 중성 청회로 올리고 노출을 키운다(평균 rgb(164,111,75), 목표 (163,119,83))
    bg: 0xb9cfc6, fog: 0xc6d5c4, fogNear: 40, fogFar: 120, hemiSky: 0xbccad8, hemiGround: 0x6a5a44, hemi: 1.35,
    sun: 0xffd4b0, sunI: 5.0, sunX: -13, sunY: 22, sunZ: -14, bloom: 0.7, threshold: 0.9, haze: 0xc9d8c8, exposure: 1.15,
    glow: 0xfff0c8, glowI: 0.16, glowX: 0.08, glowY: -0.08, sat: 1.04,
    // 그늘은 이끼 청록, 햇빛은 금빛: 진흙빛 그늘(심사 지적)을 차가운 색으로 들어 올린다
    shadowTint: 0x8fa8c0, highTint: 0xffe2b0, split: 0.35, lift: 0.03,
  },
  r2: {
    bg: 0xad431c, fog: 0x351b1c, fogNear: 38, fogFar: 115, hemiSky: 0xb8cbe8, hemiGround: 0x71514b, hemi: 2.0,
    sun: 0xffe3c4, sunI: 3.8, sunX: -10, sunY: 14, sunZ: -24, bloom: 0.45, threshold: 1.1, haze: 0x6e291e, exposure: 1.05,
    glow: 0xff671b, glowI: 0.06, glowX: 0.25, glowY: -0.1, sat: 0.98,
    shadowTint: 0xa9bfd7, highTint: 0xffefdb, split: 0.12, lift: 0.025,
  },
  // ── D3a ──
  // R3 서리 묘지: 흐린 겨울 오후. 하늘빛을 푸르게, 해는 창백하게 낮춰 그림자를 차갑게, 채도를 줄여 묘지 돌·눈이 청회로 읽힌다.
  // c023: 지역판 키트·텍스처(이미 청회·청록으로 칠함)에 맞춰 채도를 되살리고(0.82 → 0.96) 노출을 조금 낮춰 눈이 하얗게 날지 않게,
  // 그늘은 더 푸르게(콘셉트 r3-dungeon의 짙은 청색 그늘 + 밝은 눈 대비)
  r3: {
    bg: 0x7f98c0, fog: 0x98aecb, fogNear: 30, fogFar: 108, hemiSky: 0xc4d8f4, hemiGround: 0x404a64, hemi: 1.55,
    sun: 0xe8f0ff, sunI: 3.9, sunX: -12, sunY: 20, sunZ: -16, bloom: 0.62, threshold: 0.95, haze: 0x9fb4d2, exposure: 1.05,
    glow: 0xb4e2ff, glowI: 0.14, glowX: 0.1, glowY: -0.08, sat: 0.96,
    shadowTint: 0x6f8cc8, highTint: 0xeef5ff, split: 0.42, lift: 0.03,
  },
  // R4 가라앉은 사원: 습한 늪 안개. 안개를 가깝게 두고 반구광 바닥을 진흙빛으로, 햇무리를 연두로 돌려 독안개 느낌을 준다.
  // c023: 지역판 늪 키트가 이미 녹회라 안개·반구광의 녹색을 덜어 회녹 대비를 살리고(벽·바닥이 한 녹색 덩어리로 묻혔다) 노출을 올린다
  r4: {
    bg: 0x60705a, fog: 0x5f7058, fogNear: 28, fogFar: 100, hemiSky: 0xc2d2bc, hemiGround: 0x3e3c2c, hemi: 1.6,
    sun: 0xf4f0d2, sunI: 4.1, sunX: -8, sunY: 18, sunZ: -20, bloom: 0.62, threshold: 0.95, haze: 0x6f8466, exposure: 1.16,
    glow: 0xbaff7c, glowI: 0.06, glowX: 0.2, glowY: -0.1, sat: 0.94,
    shadowTint: 0x6f8a86, highTint: 0xf6f4dc, split: 0.35, lift: 0.03,
  },
  // ── 3개월 C2 ──
  // R5 황금 모래 폐도: 늦은 오후 사막. 낮은 금빛 해를 오른쪽 뒤에 두어 열주·오벨리스크 그림자가 길게 눕고, 그늘은 청록 하늘빛(콘셉트 map-r5 "long cool blue shadows").
  // 모래가 하얗게 날지 않게 노출은 마을 수준, 햇무리는 호박색, 분할 톤으로 그늘 청·햇빛 금 대비를 크게 준다
  r5: {
    bg: 0xe2b97a, fog: 0xe0bd8a, fogNear: 36, fogFar: 118, hemiSky: 0x9fb8d6, hemiGround: 0x8a6a40, hemi: 1.3,
    sun: 0xffd09a, sunI: 5.2, sunX: 12, sunY: 14, sunZ: -20, bloom: 0.6, threshold: 0.95, haze: 0xe6c898, exposure: 1.1,
    glow: 0xffb45a, glowI: 0.2, glowX: 0.88, glowY: -0.06, sat: 1.02,
    shadowTint: 0x7fa0c8, highTint: 0xffe2a8, split: 0.42, lift: 0.025,
  },
  // R6 심연의 성채: 허공 위 보랏빛 밤. 해 대신 차가운 달빛(청보라)을 높이 두고 반구광 바닥을 보라로 — 흑요석 위에 보라 림이 걸린다.
  // 안개는 가깝고 짙은 남보라(아래 허공이 끝없이 깊어 보이게), 햇무리는 자홍, 블룸 문턱을 낮춰 공허 불꽃이 번진다
  r6: {
    bg: 0x241a3a, fog: 0x2a1f44, fogNear: 30, fogFar: 104, hemiSky: 0xb8a8ec, hemiGround: 0x3a2050, hemi: 1.75,
    sun: 0xd8ccff, sunI: 3.6, sunX: -10, sunY: 20, sunZ: -18, bloom: 0.75, threshold: 0.85, haze: 0x3a2a5c, exposure: 1.12,
    glow: 0xc060ff, glowI: 0.12, glowX: 0.15, glowY: -0.1, sat: 1.0,
    shadowTint: 0x6a5aa8, highTint: 0xf0e4ff, split: 0.4, lift: 0.035,
  },
};

/** 마을②~④ 분위기(마을①은 MAP_MOOD.village). 같은 광장 배치에 시간·날씨만 바꿔 마을이 달라 보이게 한다(PRD 2.2 "마을은 실제로 바뀐다") */
export const TOWN_MOOD: Readonly<Record<number, MapMood>> = {
  // ② 잿불 전초기지: 잿불 노을. 해를 붉게 낮추고 안개에 재 섞인 주황
  2: { ...MAP_MOOD.village, bg: 0xd6875c, fog: 0xc98a64, hemiSky: 0xa8a6c0, hemiGround: 0x7a4428, sun: 0xffb27a, sunI: 5.0, haze: 0xd59a6e, glow: 0xff8a3d, glowI: 0.24, sat: 0.96, highTint: 0xffd6a8 },
  // ③ 서리 항구: 맑은 겨울 아침. 푸른 하늘빛, 흰 해
  3: { ...MAP_MOOD.village, bg: 0xb4c8e0, fog: 0xc4d4e6, hemiSky: 0xc4d8f2, hemiGround: 0x5a6070, sun: 0xf2f6ff, sunI: 5.0, haze: 0xcad8ea, glow: 0xd8ecff, glowI: 0.16, sat: 0.86, shadowTint: 0x88a2cc, highTint: 0xf0f6ff, exposure: 1.12 },
  // ④ 늪지 장터: 흐린 습지 오후. 연두 안개, 낮은 대비
  4: { ...MAP_MOOD.village, bg: 0x9fb08a, fog: 0x93a67e, fogNear: 30, fogFar: 100, hemiSky: 0xb6caa6, hemiGround: 0x4c4a2e, sun: 0xf0f2c8, sunI: 4.4, haze: 0x9cb088, glow: 0xd2ff9a, glowI: 0.12, sat: 0.92, shadowTint: 0x7f9a7c, highTint: 0xf4f8da },
  // ⑤ 오아시스 대상단(C2): 사막 해질녘. 금빛 해, 청록 그늘, 따뜻한 먼지 안개
  5: { ...MAP_MOOD.village, bg: 0xf0b878, fog: 0xe8bf8c, hemiSky: 0x98b6d4, hemiGround: 0x8a5e30, sun: 0xffc488, sunI: 5.4, haze: 0xf0cc98, glow: 0xffa848, glowI: 0.24, sat: 1.04, shadowTint: 0x7aa4c4, highTint: 0xffdca0 },
  // ⑥ 하늘 요새(C2): 구름 위 맑은 한낮. 흰 해, 푸른 하늘빛, 멀리 보랏빛 성채 쪽 안개
  6: { ...MAP_MOOD.village, bg: 0xb8d4f2, fog: 0xcadcf2, fogNear: 40, fogFar: 130, hemiSky: 0xcfe0fa, hemiGround: 0x6a6a86, sun: 0xfff6e8, sunI: 5.2, sunY: 20, haze: 0xd8e4f6, glow: 0xe0eaff, glowI: 0.18, sat: 0.94, shadowTint: 0x8c9ed0, highTint: 0xfff8ee, exposure: 1.14 },
};

/**
 * 지역1 스테이지 지도 분위기(S2): 같은 이끼 유적 낮 분위기(MAP_MOOD.r1)에서 지도·시간대마다 해 방향·색·안개를 바꿔 6판이 색부터 달라 보이게 한다.
 *  a  맑은 한낮 · a2 해질녘(낮은 주황 해, 보랏빛 그늘) · m 따뜻한 먼지 낀 오후(모닥불 연기) · b 청록 물빛 한낮 · b2 아침 안개(차갑고 옅은 해) · f 황금 빛줄기 숲 그늘
 */
const R1 = MAP_MOOD.r1;
export const STAGE_MOOD: Readonly<Record<StageMapKey, MapMood>> = {
  a: { ...R1, bg: 0xb4d0d4, fog: 0xc4d8d0, haze: 0xc4dad0, sun: 0xffe2c4, sunI: 5.2, exposure: 1.14 },
  a2: {
    ...R1, bg: 0xe2a97c, fog: 0xdcaa84, fogNear: 34, fogFar: 112, hemiSky: 0xb0a8c4, hemiGround: 0x6a4636, hemi: 1.15,
    sun: 0xffb27a, sunI: 4.7, sunX: 12, sunY: 9, sunZ: -20, bloom: 0.8, threshold: 0.9, haze: 0xe6b48c, exposure: 1.12,
    glow: 0xffa050, glowI: 0.26, glowX: 0.92, glowY: -0.04, sat: 1.0, shadowTint: 0x8472a4, highTint: 0xffc488, split: 0.48, lift: 0.035,
  },
  m: {
    ...R1, bg: 0xd0b890, fog: 0xcdbd98, fogNear: 34, fogFar: 108, hemiSky: 0xcfc4b0, hemiGround: 0x6e4e30, hemi: 1.2,
    sun: 0xffd8aa, sunI: 4.9, sunX: -12, sunY: 16, sunZ: -16, bloom: 0.8, haze: 0xd6c4a0, exposure: 1.12,
    glow: 0xffb468, glowI: 0.2, sat: 1.08, shadowTint: 0x9a8fa4, highTint: 0xffdca4, split: 0.4, lift: 0.03,
  },
  b: {
    ...R1, bg: 0xaed4de, fog: 0xc0dce2, fogNear: 42, fogFar: 128, hemiSky: 0xc4e0f2, hemiGround: 0x4e6a5e, hemi: 1.45,
    sun: 0xfff0d2, sunI: 5.0, sunX: 10, sunY: 22, sunZ: -14, bloom: 0.72, haze: 0xbfdde0, exposure: 1.14,
    glow: 0xe2f6ff, glowI: 0.2, sat: 1.06, shadowTint: 0x7fa6c0, highTint: 0xfff0cc, split: 0.38,
  },
  b2: {
    ...R1, bg: 0xc9d6e4, fog: 0xd6e1ea, fogNear: 22, fogFar: 92, hemiSky: 0xd2dff0, hemiGround: 0x586a70, hemi: 1.5,
    sun: 0xfff2de, sunI: 3.3, sunX: -16, sunY: 12, sunZ: -18, bloom: 0.62, haze: 0xd8e3ec, exposure: 1.1,
    glow: 0xfff0dc, glowI: 0.14, glowX: 0.12, glowY: -0.04, sat: 0.94, shadowTint: 0x93aac8, highTint: 0xfff0d4, split: 0.36,
  },
  f: {
    ...R1, bg: 0xb4cca0, fog: 0xbed4a4, fogNear: 36, fogFar: 112, hemiSky: 0xc8e0c0, hemiGround: 0x5e4c2c, hemi: 1.3,
    sun: 0xffe6b0, sunI: 5.6, sunX: -10, sunY: 24, sunZ: -12, bloom: 0.9, threshold: 0.85, haze: 0xcfe0b0, exposure: 1.17,
    glow: 0xfff0b0, glowI: 0.26, sat: 1.08, shadowTint: 0x7fa088, highTint: 0xfff0b6, split: 0.38, lift: 0.03,
  },
};

/**
 * 지역2 스테이지 지도 분위기(S6): 잿불 협곡 기본(MAP_MOOD.r2)에서 지도·시간대마다 바꾼다. 아래 용암 바다가 주광이라 반구광 바닥색을 붉게,
 * 해는 연기 낀 하늘에서 비스듬히 비추는 약한 주광이다.
 *  a  깊은 협곡 한낮(검은 연기 하늘, 주황 지평) · a2 붉은 노을(낮은 핏빛 해, 보랏빛 그늘) · m 화로 안(짙은 연기·주황 열기·높은 블룸)
 *  b  용암 강 한낮(밝은 주황 반사, 노란 안개) · b2 잿빛 새벽(재가 내리는 회청 안개, 차가운 해) · f 제단 해자(진홍 안개·보랏빛 하늘·강한 햇무리)
 */
const R2 = MAP_MOOD.r2;
export const R2_STAGE_MOOD: Readonly<Record<StageMapKey, MapMood>> = {
  a: {
    ...R2, bg: 0x8a3418, fog: 0x7a3016, fogNear: 38, fogFar: 118, hemiSky: 0xd0c0b4, hemiGround: 0xb0501e, hemi: 1.95,
    sun: 0xffd8b0, sunI: 3.8, sunX: -10, sunY: 16, sunZ: -22, bloom: 0.65, threshold: 1.0, haze: 0x9a3c1a, exposure: 1.16,
    glow: 0xff7a2a, glowI: 0.1, glowX: 0.3, glowY: -0.1, sat: 1.02, shadowTint: 0x9aa8c8, highTint: 0xffe8d0, split: 0.18, lift: 0.025,
  },
  a2: {
    ...R2, bg: 0x7a2428, fog: 0x6c2228, fogNear: 34, fogFar: 112, hemiSky: 0xbca0b8, hemiGround: 0xa03a20, hemi: 1.75,
    sun: 0xff8a5a, sunI: 4.0, sunX: 14, sunY: 8, sunZ: -20, bloom: 0.75, threshold: 0.95, haze: 0x9a3030, exposure: 1.14,
    glow: 0xff5a3a, glowI: 0.24, glowX: 0.9, glowY: -0.05, sat: 1.04, shadowTint: 0x8a74b0, highTint: 0xffc49a, split: 0.4, lift: 0.03,
  },
  m: {
    ...R2, bg: 0x6a2c14, fog: 0x6a3016, fogNear: 30, fogFar: 104, hemiSky: 0xc8b4a4, hemiGround: 0xc0581e, hemi: 1.85,
    sun: 0xffc890, sunI: 3.5, sunX: -12, sunY: 18, sunZ: -14, bloom: 0.85, threshold: 0.95, haze: 0x904018, exposure: 1.16,
    glow: 0xff8a30, glowI: 0.2, glowX: 0.5, glowY: -0.12, sat: 1.06, shadowTint: 0x8c8aa8, highTint: 0xffd8a8, split: 0.3, lift: 0.03,
  },
  b: {
    ...R2, bg: 0x9a4a1c, fog: 0x8a4218, fogNear: 42, fogFar: 128, hemiSky: 0xd8ccc0, hemiGround: 0xc85a20, hemi: 1.95,
    sun: 0xfff0d0, sunI: 3.9, sunX: 10, sunY: 20, sunZ: -16, bloom: 0.7, threshold: 1.0, haze: 0xaa5420, exposure: 1.14,
    glow: 0xffa040, glowI: 0.16, glowX: 0.7, glowY: -0.08, sat: 1.04, shadowTint: 0x9aa4c0, highTint: 0xfff0d4, split: 0.25, lift: 0.025,
  },
  b2: {
    ...R2, bg: 0x5a5458, fog: 0x6a6266, fogNear: 26, fogFar: 98, hemiSky: 0xc8d0dc, hemiGround: 0x8a4a30, hemi: 1.75,
    sun: 0xe8eeff, sunI: 3.0, sunX: -16, sunY: 12, sunZ: -18, bloom: 0.62, threshold: 1.0, haze: 0x6e6468, exposure: 1.1,
    glow: 0xff9a5a, glowI: 0.1, glowX: 0.15, glowY: -0.05, sat: 0.92, shadowTint: 0x8ea0c8, highTint: 0xf2f0ec, split: 0.36, lift: 0.03,
  },
  f: {
    ...R2, bg: 0x6a2030, fog: 0x6e2430, fogNear: 36, fogFar: 118, hemiSky: 0xc4acd0, hemiGround: 0xb8401e, hemi: 1.9,
    sun: 0xffd2b0, sunI: 3.8, sunX: -8, sunY: 22, sunZ: -12, bloom: 0.9, threshold: 0.9, haze: 0x9a2c38, exposure: 1.16,
    glow: 0xff6a3a, glowI: 0.22, glowX: 0.5, glowY: -0.14, sat: 1.06, shadowTint: 0x8a7cbc, highTint: 0xffdcc0, split: 0.34, lift: 0.03,
  },
};

/** 지도의 분위기(마을은 마을 번호별, 스테이지 지도는 지역별 STAGE_MOOD·R2_STAGE_MOOD). 지도 객체는 levels.VILLAGES 순서로 마을 번호를 안다 */
export function moodOf(map: MapDef): MapMood {
  if (map.stageKey) return map.id === 'r2' ? R2_STAGE_MOOD[map.stageKey] : STAGE_MOOD[map.stageKey];
  if (map.id !== 'village') return MAP_MOOD[map.id];
  return TOWN_MOOD[VILLAGES.indexOf(map) + 1] ?? MAP_MOOD.village;
}

/**
 * 환경 색 보정(D3a, 키트·바닥 재사용 테마): 같은 유적 키트·판석 텍스처를 지역마다 다르게 보이게 한다.
 * kit = 키트 인스턴스 색(곱), floor = 바닥 색(곱)·채도, snow = 서리 얼룩 세기(0..1), wet = 젖은 얼룩 세기 가산(늪)
 */
export interface EnvGrade {
  readonly kit: number;
  readonly floor: readonly [number, number, number];
  readonly floorSat: number;
  readonly snow: number;
  readonly wet: number;
}
const TOWN_GRADE: Readonly<Record<number, EnvGrade>> = {
  2: { kit: 0xf2d8c8, floor: [1.0, 0.92, 0.84], floorSat: 0.9, snow: 0, wet: 0 },
  3: { kit: 0xdde6f2, floor: [0.94, 0.98, 1.04], floorSat: 0.72, snow: 0.28, wet: 0 },
  4: { kit: 0xd6e2c6, floor: [0.9, 0.96, 0.84], floorSat: 0.84, snow: 0, wet: 0.12 },
  5: { kit: 0xf6e0b8, floor: [1.04, 0.96, 0.82], floorSat: 0.92, snow: 0, wet: 0 },
  6: { kit: 0xe4ecf8, floor: [0.98, 1.0, 1.04], floorSat: 0.8, snow: 0, wet: 0 },
};
const MAP_GRADE: Partial<Record<MapId, EnvGrade>> = {
  r3: { kit: 0xc6d6ee, floor: [0.86, 0.93, 1.06], floorSat: 0.42, snow: 0.6, wet: 0 },
  r4: { kit: 0xa9c29a, floor: [0.74, 0.88, 0.66], floorSat: 0.78, snow: 0, wet: 0.3 },
  // C2: 지역판 키트가 없을 때(?envlegacy) r1 유적 키트를 사암·흑요석 쪽으로 곱한다
  r5: { kit: 0xf0d4a0, floor: [1.08, 0.96, 0.76], floorSat: 0.86, snow: 0, wet: 0 },
  r6: { kit: 0x6e6088, floor: [0.5, 0.46, 0.62], floorSat: 0.55, snow: 0, wet: 0 },
};
/**
 * 지역판 키트·텍스처(c023)를 쓸 때의 보정. 새 키트·판석은 이미 지역 색(청회 눈·녹회 이끼)으로 칠해져 있어
 * 위 MAP_GRADE의 청회·이끼빛 곱을 또 하면 탁해진다 → 키트 곱은 거의 흰색, 바닥은 채도만 살짝 정리하고 서리·물기만 얹는다.
 */
const REGION_GRADE: Partial<Record<MapId, EnvGrade>> = {
  r3: { kit: 0xf4f7ff, floor: [0.98, 1.0, 1.03], floorSat: 0.92, snow: 0.36, wet: 0 },
  r4: { kit: 0xf2f6ea, floor: [1.0, 1.02, 0.97], floorSat: 0.95, snow: 0, wet: 0.1 },
  r5: { kit: 0xfff8ec, floor: [1.02, 1.0, 0.97], floorSat: 0.96, snow: 0, wet: 0 },
  r6: { kit: 0xf4f0ff, floor: [0.98, 0.98, 1.03], floorSat: 0.94, snow: 0, wet: 0 },
};
export function gradeOf(map: MapDef, regionKits = false): EnvGrade | null {
  if (map.id === 'village') return TOWN_GRADE[VILLAGES.indexOf(map) + 1] ?? null;
  if (regionKits) return REGION_GRADE[map.id] ?? MAP_GRADE[map.id] ?? null;
  return MAP_GRADE[map.id] ?? null;
}
